// ZassoSessionManager — Koe Zasso (thought-coach) WebRTC conversation.
//
// spec §2.3.3:
//   1. POST /api/session/chat → ephemeral "ek_..." + session config.
//   2. Capture mic (continuous) + play remote audio track (assistant voice).
//   3. RTCPeerConnection + addTrack + createDataChannel("oai-events").
//   4. SDP exchange against https://api.openai.com/v1/realtime/calls.
//   5. Hands-free via server_vad (config done server-side in the mint body).
//   6. Conversation history mirrored locally for save/download.
//
// Failsafes (spec §1.4 U-ZAS-8):
//   - MAX_SESSION_MIN: force stop at 15 minutes from start.
//   - SILENCE_TIMEOUT_MIN: force stop after 3 minutes with no user speech
//     activity (measured from last speech_stopped / response.done).

import type {
  ChatSessionResponse,
  ZassoVoice,
} from "../../lib/api-types";
import type { ZassoServerEvent } from "./events";

const REALTIME_SDP_URL = "https://api.openai.com/v1/realtime/calls";

// ── Failsafes ──────────────────────────────────────────────────
export const MAX_SESSION_MS = 15 * 60 * 1000;
export const SILENCE_TIMEOUT_MS = 3 * 60 * 1000;
const FAILSAFE_POLL_MS = 1000;

// ── State ──────────────────────────────────────────────────────
export type ZassoState =
  | "idle"
  | "starting"
  | "listening"
  | "thinking"
  | "speaking"
  | "stopping"
  | "error"
  | "closed";

export type AutoTerminationReason = "max_session" | "silence_timeout";

export interface ZassoSegment {
  role: "user" | "assistant";
  text: string;
  completedAt: number;
}

export interface ZassoStopResult {
  turns: ZassoSegment[];
  durationSec: number;
  /** Assistant-turn count — mirrors the spec's "turns" language. */
  assistantTurnCount: number;
  /** True when either currentUserDelta or currentAssistantDelta was
   *  force-pushed on stop. Surfaces "末尾未確定" UX hint. */
  hadPendingDelta: boolean;
  /** Reason for auto-termination, if any. */
  autoTerminationReason?: AutoTerminationReason;
}

export interface ZassoSessionCallbacks {
  onStateChange?: (state: ZassoState) => void;
  onConversationChange?: () => void;
  onRemoteTrack?: (stream: MediaStream) => void;
  onSessionStarted?: () => void;
  onSessionEnded?: () => void;
  onAutoTerminated?: (reason: AutoTerminationReason) => void;
  onError?: (message: string) => void;
  /** Fired when a function_call begins (output_item.added). */
  onToolStarted?: (name: string) => void;
  /** Fired when a function_call completes (output sent back) or aborts. */
  onToolFinished?: (name: string) => void;
}

export class ZassoSessionManager {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private localStream: MediaStream | null = null;
  private micTrack: MediaStreamTrack | null = null;

  private state: ZassoState = "idle";

  private startedAt: number | null = null;
  /** Last user speech activity — used for silence timeout. */
  private lastSpeechAt: number | null = null;

  private readonly segments: ZassoSegment[] = [];
  private currentUserDelta = "";
  private currentAssistantDelta = "";

  private failsafeTimer: number | null = null;
  private autoTerminationReason: AutoTerminationReason | null = null;

  private lastStopResult: ZassoStopResult | null = null;
  private abortStart = false;

  /** In-flight function_call metadata keyed by call_id. `handled` is set
   *  once the call has been dispatched, so the matching
   *  response.output_item.done doesn't double-fire. */
  private readonly pendingFunctionCalls = new Map<
    string,
    {
      name: string;
      /** Buffer we accumulate from `.delta` chunks; `.done` overrides it
       *  if the server sends the finalised string. */
      argsBuffer: string;
      handled: boolean;
    }
  >();

  /** Name of the currently executing tool — UI surface via getActiveToolName().
   *  We only track one at a time for the UI indicator even if the model
   *  issues parallel calls; multiple calls still flow correctly, this is
   *  purely a display affordance. */
  private activeToolName: string | null = null;

  constructor(private readonly cb: ZassoSessionCallbacks = {}) {}

  // ── Getters ────────────────────────────────────────────────
  getState(): ZassoState {
    return this.state;
  }

  getStartedAt(): number | null {
    return this.startedAt;
  }

  getDurationSec(): number {
    if (this.startedAt == null) return 0;
    return (Date.now() - this.startedAt) / 1000;
  }

  getSegments(): ReadonlyArray<ZassoSegment> {
    return this.segments;
  }

  getCurrentUserDelta(): string {
    return this.currentUserDelta;
  }

  getCurrentAssistantDelta(): string {
    return this.currentAssistantDelta;
  }

  /** Live view including the in-flight deltas for whichever role is active. */
  getLiveConversation(): ZassoSegment[] {
    const live = [...this.segments];
    if (this.currentUserDelta) {
      live.push({
        role: "user",
        text: this.currentUserDelta,
        completedAt: Date.now(),
      });
    }
    if (this.currentAssistantDelta) {
      live.push({
        role: "assistant",
        text: this.currentAssistantDelta,
        completedAt: Date.now(),
      });
    }
    return live;
  }

  /** Confirmed-only view for persistence. */
  getFinalConversation(): ZassoSegment[] {
    return [...this.segments];
  }

  /** Tool currently executing, if any — for the UI status line. */
  getActiveToolName(): string | null {
    return this.activeToolName;
  }

  // ── Public lifecycle ───────────────────────────────────────
  async start(voice: ZassoVoice, audioInputId?: string | null): Promise<void> {
    if (this.state !== "idle" && this.state !== "closed") {
      throw new Error(`cannot start from state ${this.state}`);
    }
    this.resetForNewSession();
    this.setState("starting");
    this.abortStart = false;

    try {
      // 1. Mint ephemeral secret via our Worker.
      const tokenRes = await fetch("/api/session/chat", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ voice }),
      });
      if (this.abortStart) return this.rollbackPartialStart();
      if (!tokenRes.ok) {
        const text = await tokenRes.text().catch(() => "");
        throw new Error(
          `mint ephemeral token failed: ${tokenRes.status} ${text.slice(0, 200)}`,
        );
      }
      const session = (await tokenRes.json()) as ChatSessionResponse;

      // 2. Capture microphone (continuous — no PTT, no enable toggling).
      const audioConstraints: MediaTrackConstraints = audioInputId
        ? { deviceId: { exact: audioInputId } }
        : {};
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...audioConstraints,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      if (this.abortStart) return this.rollbackPartialStart();

      const track = this.localStream.getAudioTracks()[0];
      if (!track) throw new Error("no audio track from getUserMedia");
      this.micTrack = track;
      track.enabled = true;

      // 3. RTCPeerConnection + DataChannel + tracks.
      this.pc = new RTCPeerConnection();

      this.pc.addEventListener("connectionstatechange", () => {
        const s = this.pc?.connectionState;
        if (
          (s === "failed" || s === "disconnected") &&
          this.state !== "stopping" &&
          this.state !== "closed"
        ) {
          this.setState("error");
          this.cb.onError?.(`peer connection ${s}`);
        }
      });

      // Assistant voice arrives as a remote track → play via <audio>.
      this.pc.addEventListener("track", (ev) => {
        const [stream] = ev.streams;
        if (stream) this.cb.onRemoteTrack?.(stream);
      });

      this.pc.addTrack(track, this.localStream);

      this.dc = this.pc.createDataChannel("oai-events");
      this.dc.addEventListener("open", () => {
        if (this.state === "starting") {
          // First reachable conversational state is "listening" —
          // server_vad drives further transitions.
          this.setState("listening");
        }
      });
      this.dc.addEventListener("message", (ev) => {
        this.handleDataChannelMessage(ev.data);
      });
      this.dc.addEventListener("error", (ev) => {
        console.error("[zasso] datachannel error", ev);
      });

      // 4. SDP exchange.
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      if (this.abortStart) return this.rollbackPartialStart();

      const sdpRes = await fetch(REALTIME_SDP_URL, {
        method: "POST",
        headers: {
          authorization: `Bearer ${session.clientSecret}`,
          "content-type": "application/sdp",
        },
        body: offer.sdp ?? "",
      });
      if (this.abortStart) return this.rollbackPartialStart();
      if (!sdpRes.ok) {
        const text = await sdpRes.text().catch(() => "");
        throw new Error(
          `SDP exchange failed: ${sdpRes.status} ${text.slice(0, 200)}`,
        );
      }
      const answerSdp = await sdpRes.text();
      await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      if (this.abortStart) return this.rollbackPartialStart();

      // 5. Session bookkeeping + failsafe timer.
      this.startedAt = Date.now();
      this.lastSpeechAt = this.startedAt;
      this.startFailsafeTimer();
      this.cb.onSessionStarted?.();
    } catch (err) {
      this.setState("error");
      this.closeConnection();
      const msg = err instanceof Error ? err.message : String(err);
      this.cb.onError?.(msg);
      throw err;
    }
  }

  /** Gracefully end the conversation. Idempotent. */
  async stop(reason?: AutoTerminationReason): Promise<ZassoStopResult> {
    if (this.state === "closed" && this.lastStopResult) {
      return this.lastStopResult;
    }
    if (this.state === "stopping") {
      while (this.state === "stopping") {
        await new Promise((r) => setTimeout(r, 50));
      }
      return this.lastStopResult ?? this.emptyStopResult();
    }

    if (this.state === "starting") {
      this.abortStart = true;
      this.setState("stopping");
      this.stopFailsafeTimer();
      this.closeConnection();
      const result = this.emptyStopResult();
      this.lastStopResult = result;
      this.setState("closed");
      this.cb.onSessionEnded?.();
      return result;
    }

    if (this.state === "idle" || this.state === "error") {
      this.stopFailsafeTimer();
      this.closeConnection();
      const result = this.finaliseConversation();
      this.lastStopResult = result;
      this.setState("closed");
      return result;
    }

    // Normal wind-down from listening / thinking / speaking.
    if (reason) this.autoTerminationReason = reason;
    this.setState("stopping");
    this.stopFailsafeTimer();

    if (this.micTrack) this.micTrack.enabled = false;

    // Brief grace so a trailing transcript has a chance to land. Zasso has
    // no explicit "settle" semantics from OpenAI here; a 1s window is a
    // reasonable compromise (the assistant side is usually already idle).
    await new Promise((r) => setTimeout(r, 1000));

    this.flushPendingDeltas();
    this.closeConnection();

    const result = this.finaliseConversation();
    this.lastStopResult = result;
    this.setState("closed");
    this.cb.onSessionEnded?.();
    return result;
  }

  // ── Internals ──────────────────────────────────────────────
  private resetForNewSession() {
    this.segments.length = 0;
    this.currentUserDelta = "";
    this.currentAssistantDelta = "";
    this.startedAt = null;
    this.lastSpeechAt = null;
    this.autoTerminationReason = null;
    this.lastStopResult = null;
    this.pendingFunctionCalls.clear();
    this.activeToolName = null;
  }

  private rollbackPartialStart(): void {
    this.stopFailsafeTimer();
    this.closeConnection();
    this.setState("closed");
  }

  private closeConnection() {
    try {
      this.dc?.close();
    } catch {
      /* ignore */
    }
    this.dc = null;
    try {
      this.pc?.close();
    } catch {
      /* ignore */
    }
    this.pc = null;
    this.micTrack?.stop();
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    this.micTrack = null;
  }

  private setState(next: ZassoState) {
    if (this.state === next) return;
    this.state = next;
    this.cb.onStateChange?.(next);
  }

  private emptyStopResult(): ZassoStopResult {
    return {
      turns: [],
      durationSec: 0,
      assistantTurnCount: 0,
      hadPendingDelta: false,
    };
  }

  private finaliseConversation(): ZassoStopResult {
    const assistantTurnCount = this.segments.filter(
      (s) => s.role === "assistant",
    ).length;
    const result: ZassoStopResult = {
      turns: [...this.segments],
      durationSec: this.getDurationSec(),
      assistantTurnCount,
      hadPendingDelta: false,
      ...(this.autoTerminationReason
        ? { autoTerminationReason: this.autoTerminationReason }
        : {}),
    };
    return result;
  }

  private flushPendingDeltas() {
    let flushed = false;
    if (this.currentUserDelta) {
      this.segments.push({
        role: "user",
        text: this.currentUserDelta,
        completedAt: Date.now(),
      });
      this.currentUserDelta = "";
      flushed = true;
    }
    if (this.currentAssistantDelta) {
      this.segments.push({
        role: "assistant",
        text: this.currentAssistantDelta,
        completedAt: Date.now(),
      });
      this.currentAssistantDelta = "";
      flushed = true;
    }
    if (flushed) this.cb.onConversationChange?.();
  }

  // ── Failsafe timer ─────────────────────────────────────────
  private startFailsafeTimer() {
    this.stopFailsafeTimer();
    this.failsafeTimer = window.setInterval(() => {
      if (this.startedAt == null) return;
      const now = Date.now();
      if (now - this.startedAt >= MAX_SESSION_MS) {
        this.handleAutoTermination("max_session");
        return;
      }
      if (
        this.lastSpeechAt != null &&
        now - this.lastSpeechAt >= SILENCE_TIMEOUT_MS
      ) {
        this.handleAutoTermination("silence_timeout");
      }
    }, FAILSAFE_POLL_MS);
  }

  private stopFailsafeTimer() {
    if (this.failsafeTimer !== null) {
      window.clearInterval(this.failsafeTimer);
      this.failsafeTimer = null;
    }
  }

  private handleAutoTermination(reason: AutoTerminationReason) {
    this.stopFailsafeTimer();
    this.cb.onAutoTerminated?.(reason);
    // stop() runs asynchronously; fire-and-forget is fine because state
    // transitions past "stopping" are already handled by stop() itself.
    void this.stop(reason);
  }

  // ── DataChannel event handler ──────────────────────────────
  private handleDataChannelMessage(raw: unknown) {
    if (typeof raw !== "string") return;
    let parsed: ZassoServerEvent;
    try {
      parsed = JSON.parse(raw) as ZassoServerEvent;
    } catch (err) {
      console.warn("[zasso] non-JSON dc message", err);
      return;
    }

    switch (parsed.type) {
      case "input_audio_buffer.speech_started": {
        // User started speaking — if we were mid-speak, the server handles
        // the interrupt via interrupt_response:true. UI moves to listening.
        this.lastSpeechAt = Date.now();
        if (this.state !== "stopping" && this.state !== "closed") {
          this.setState("listening");
        }
        break;
      }
      case "input_audio_buffer.speech_stopped": {
        this.lastSpeechAt = Date.now();
        if (this.state === "listening") {
          this.setState("thinking");
        }
        break;
      }
      case "conversation.item.input_audio_transcription.delta": {
        const delta = (parsed as { delta?: string }).delta ?? "";
        if (delta) {
          this.currentUserDelta += delta;
          this.cb.onConversationChange?.();
        }
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const text = (parsed as { transcript?: string }).transcript ?? "";
        if (text) {
          this.segments.push({
            role: "user",
            text,
            completedAt: Date.now(),
          });
          this.currentUserDelta = "";
          this.cb.onConversationChange?.();
        }
        break;
      }
      case "response.created": {
        // Tracked for completeness; state moves to speaking on the first
        // transcript delta so we don't flash THINK → SPEAK prematurely.
        break;
      }
      case "response.output_audio_transcript.delta": {
        const delta = (parsed as { delta?: string }).delta ?? "";
        if (delta) {
          this.currentAssistantDelta += delta;
          if (this.state === "thinking" || this.state === "listening") {
            this.setState("speaking");
          }
          this.cb.onConversationChange?.();
        }
        break;
      }
      case "response.output_audio_transcript.done": {
        const text = (parsed as { transcript?: string }).transcript ?? "";
        if (text || this.currentAssistantDelta) {
          this.segments.push({
            role: "assistant",
            text: text || this.currentAssistantDelta,
            completedAt: Date.now(),
          });
          this.currentAssistantDelta = "";
          this.cb.onConversationChange?.();
        }
        break;
      }
      case "response.done": {
        // AI turn finished — the assistant transcript is already flushed by
        // `.done`; move back to listening while the mic keeps streaming.
        if (this.state === "speaking" || this.state === "thinking") {
          this.setState("listening");
        }
        break;
      }
      case "response.output_item.added": {
        const item = (parsed as { item?: {
          type?: string;
          call_id?: string;
          name?: string;
          arguments?: string;
        } }).item;
        if (
          item?.type === "function_call" &&
          typeof item.call_id === "string" &&
          typeof item.name === "string"
        ) {
          this.pendingFunctionCalls.set(item.call_id, {
            name: item.name,
            argsBuffer: typeof item.arguments === "string" ? item.arguments : "",
            handled: false,
          });
          this.activeToolName = item.name;
          this.cb.onToolStarted?.(item.name);
        }
        break;
      }
      case "response.function_call_arguments.delta": {
        const ev = parsed as { call_id?: string; delta?: string };
        if (ev.call_id && typeof ev.delta === "string") {
          const pending = this.pendingFunctionCalls.get(ev.call_id);
          if (pending) pending.argsBuffer += ev.delta;
        }
        break;
      }
      case "response.function_call_arguments.done": {
        const ev = parsed as { call_id?: string; arguments?: string };
        if (ev.call_id) {
          const argsJson =
            typeof ev.arguments === "string"
              ? ev.arguments
              : (this.pendingFunctionCalls.get(ev.call_id)?.argsBuffer ?? "");
          // Fire-and-forget; dispatch awaits the fetch internally but we
          // do not want to block the dc message pump.
          void this.dispatchFunctionCall(ev.call_id, argsJson);
        }
        break;
      }
      case "response.output_item.done": {
        // If the call was never dispatched (e.g. malformed item), clean up
        // so pendingFunctionCalls doesn't leak across turns.
        const item = (parsed as { item?: { call_id?: string } }).item;
        const callId = item?.call_id;
        if (callId && this.pendingFunctionCalls.has(callId)) {
          const pending = this.pendingFunctionCalls.get(callId);
          if (pending && !pending.handled) {
            this.pendingFunctionCalls.delete(callId);
            if (this.activeToolName === pending.name) {
              this.activeToolName = null;
              this.cb.onToolFinished?.(pending.name);
            }
          }
        }
        break;
      }
      case "error": {
        const err = (parsed as { error?: { message?: string } }).error;
        this.cb.onError?.(err?.message ?? "realtime error");
        break;
      }
      case "session.created":
      case "session.updated":
        break;
      default:
        break;
    }
  }

  // ── Function call dispatch (T6-16) ─────────────────────────

  /** Parse the argument JSON, call the matching /api/vault/* endpoint,
   *  and feed the result back into the session so the model can answer. */
  private async dispatchFunctionCall(
    callId: string,
    argsJson: string,
  ): Promise<void> {
    const pending = this.pendingFunctionCalls.get(callId);
    if (!pending) {
      console.warn("[zasso] function_call_arguments.done for unknown call_id", callId);
      // Still send an error output so the model can move past the dangling
      // call rather than hanging forever waiting for a reply.
      this.sendFunctionCallOutput(callId, { error: "UNKNOWN_CALL_ID" });
      return;
    }
    // Mark as handled *before* the async work so the output_item.done
    // path cannot cancel us mid-flight.
    pending.handled = true;

    let endpoint: string;
    if (pending.name === "search_notes") {
      endpoint = "/api/vault/search";
    } else if (pending.name === "read_note") {
      endpoint = "/api/vault/read";
    } else {
      console.warn("[zasso] unknown tool name", pending.name);
      this.sendFunctionCallOutput(callId, { error: "UNKNOWN_FUNCTION" });
      this.finishFunctionCall(callId, pending.name);
      return;
    }

    let args: Record<string, unknown>;
    try {
      args = argsJson ? JSON.parse(argsJson) : {};
    } catch (err) {
      console.error("[zasso] failed to parse function args", err, argsJson);
      this.sendFunctionCallOutput(callId, {
        error: "INVALID_ARGUMENTS",
      });
      this.finishFunctionCall(callId, pending.name);
      return;
    }

    let result: unknown;
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(args),
      });
      result = await res.json();
    } catch (err) {
      console.error("[zasso] tool fetch failed", endpoint, err);
      result = { error: "FETCH_FAILED" };
    }

    this.sendFunctionCallOutput(callId, result);
    this.finishFunctionCall(callId, pending.name);
  }

  /** Post the result back onto the DataChannel as a function_call_output
   *  conversation item, then explicitly kick a new response.create —
   *  server_vad does not auto-continue outside of a user turn. */
  private sendFunctionCallOutput(callId: string, output: unknown): void {
    if (!this.dc || this.dc.readyState !== "open") {
      console.warn("[zasso] dc not open; skipping function_call_output", callId);
      return;
    }
    try {
      this.dc.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify(output),
          },
        }),
      );
      this.dc.send(JSON.stringify({ type: "response.create" }));
    } catch (err) {
      console.error("[zasso] failed to send function_call_output", err);
    }
  }

  private finishFunctionCall(callId: string, name: string): void {
    this.pendingFunctionCalls.delete(callId);
    if (this.activeToolName === name) {
      this.activeToolName = null;
      this.cb.onToolFinished?.(name);
    }
  }
}
